/**
 * What a duel *is*, as pure data: the roster both peers build from the one
 * handshake message, and the URL that carries a room code.
 *
 * The single hard requirement is that `duelEncounterSetup` be a pure function
 * of the `DuelSetup` the host sent — same seed, same ids, same order, same
 * spawn positions on both machines. The ids are therefore fixed (`host` /
 * `guest`) rather than "me" and "them": the encounter is one shared object
 * that happens to be stepped twice, and each peer only differs in which seat
 * it *draws* from (`encounter-views.ts`).
 *
 * Sides are fixed the same way — host is `friendly`, guest is `hostile`, on
 * both machines. That means the engine's own `EncounterStatus` reads
 * "enemyDown" for a dead guest regardless of who is looking, which is why the
 * duel's own outcome is computed from the two aircraft statuses instead
 * (`duel-outcome.ts`).
 */

import {
  REALISM_PRESETS,
  SCOUT_KEYS,
  WIND_STRENGTHS,
  aircraftLabel,
  buildAircraftSpec,
  fnv1a32,
  type AircraftKey,
  type CombatLevel,
  type EncounterAircraftSpec,
  type EncounterSetup,
  type FlightModelTier,
  type SimOptions,
  type WeatherKind,
} from "@red-opus/engine";
import type { DuelSetup, SignalRole } from "@red-opus/net";
import { type SearchLike } from "../mission/mission-launch.ts";

export const HOST_ID = "host";
export const GUEST_ID = "guest";

/** The AI's engagement tuning was measured here (`generators-sortie.ts`), and a duel wants the same air. */
export const DUEL_ALTITUDE_M = 1200;

/** Half the merge distance: each side starts this far from the midpoint, pointed at it. */
export const DUEL_SEPARATION_M = 1400;

/**
 * A small lateral offset so the two do not start on an exactly co-linear
 * head-on. Perfectly nose-to-nose is both unrealistic and the one geometry
 * where neither player can turn inside the other on the first pass.
 */
export const DUEL_OFFSET_M = 120;

export const DEFAULT_DUEL_TIER: FlightModelTier = "intermediate";

/** Every aeroplane a player may pick — the game's own flyable roster (`FIGHTER.DAT`'s 22 scouts). */
export const DUEL_AIRCRAFT: readonly AircraftKey[] = SCOUT_KEYS;

export interface DuelAircraftOption {
  readonly key: AircraftKey;
  readonly label: string;
}

export function duelAircraftOptions(): readonly DuelAircraftOption[] {
  return DUEL_AIRCRAFT.map((key) => ({ key, label: aircraftLabel(key) }));
}

export function isDuelAircraft(key: string): key is AircraftKey {
  return (DUEL_AIRCRAFT as readonly string[]).includes(key);
}

/** The seat this peer flies from, and the one it is shooting at. */
export function localIdFor(role: SignalRole): string {
  return role === "host" ? HOST_ID : GUEST_ID;
}

export function remoteIdFor(role: SignalRole): string {
  return role === "host" ? GUEST_ID : HOST_ID;
}

/**
 * The wind the duel is fought in, drawn from the seed both peers already hold
 * (AD-007k §10.4).
 *
 * A duel has no briefing to print a weather word, but it does have air, and
 * with the westerly live a duel in permanently dead air would be the one place
 * in the game where the wind does not exist. So the word is drawn the way a
 * mission's is — uniformly over `PS.EXE`'s three, `stormy` left unexposed — and
 * drawn *from the handshake seed*, which is the whole safety argument: it is a
 * pure function of a value both machines received, so both stamp the same word,
 * both resolve the same `windVelocity` at every altitude, and nothing about the
 * wind has to be exchanged or kept in step. `fnv1a32` rather than a raw modulo
 * of the seed so neighbouring seeds do not draw neighbouring weather.
 */
export function duelWeather(seed: number): WeatherKind {
  return WIND_STRENGTHS[fnv1a32("duel-weather", seed >>> 0) % WIND_STRENGTHS.length]!;
}

/**
 * Heading 0 points at −z (`mission-world.ts`'s convention), so the aeroplane
 * on the +z side flies heading 0 and the other flies π: a proper merge, the
 * same geometry the ace duel opens on.
 */
function duellist(
  id: string,
  aircraft: AircraftKey,
  tier: FlightModelTier,
  towardsNegativeZ: boolean,
  weather: WeatherKind,
): EncounterAircraftSpec {
  const flight: SimOptions = {
    aircraft,
    tier,
    realism: REALISM_PRESETS[tier],
    altitudeM: DUEL_ALTITUDE_M,
    headingRad: towardsNegativeZ ? 0 : Math.PI,
    airspeedMps: buildAircraftSpec(aircraft).vmaxMps * 0.9,
    throttle: 1,
    trimmed: true,
    weather,
  };
  const sign = towardsNegativeZ ? 1 : -1;
  return {
    id,
    side: id === HOST_ID ? "friendly" : "hostile",
    pilot: { kind: "player" },
    flight,
    offsetM: { x: DUEL_OFFSET_M * sign, y: 0, z: DUEL_SEPARATION_M * sign },
  };
}

/**
 * The identical encounter both peers create from the identical handshake.
 *
 * Every argument comes out of `setup` and nothing out of this machine: the
 * realism set is `REALISM_PRESETS[setup.tier]` rather than the local panel's,
 * the weather is drawn from `setup.seed`, and COMBAT LEVEL is stamped from the
 * handshake even though no duel reads it yet — `mission-spec.ts` stamping the
 * *player's own* realism onto every flight is exactly the bug that armoured
 * the sky in `db56e14`, and in a duel the same shape is an instant desync
 * rather than an invulnerable Albatros.
 */
export function duelEncounterSetup(setup: DuelSetup): EncounterSetup {
  const weather = duelWeather(setup.seed);
  return {
    seed: setup.seed >>> 0,
    combatLevel: setup.combatLevel,
    aircraft: [
      duellist(HOST_ID, setup.host, setup.tier, true, weather),
      duellist(GUEST_ID, setup.guest, setup.tier, false, weather),
    ],
  };
}

/**
 * The COMBAT LEVEL a duel is fought at. Fixed rather than taken from either
 * player's Realism Panel: it is a *difficulty* knob for the flak and the AI,
 * and a duel has neither — what it must never be is per-peer.
 */
export const DUEL_COMBAT_LEVEL: CombatLevel = "AVERAGE";

/**
 * Uniform [0, 1) from the platform CSPRNG. Not `Math.random`: this port bans
 * it inside the simulation (`flight/rng.ts`) and there is no reason to make an
 * exception for the two places outside it that need entropy — a room code
 * somebody could otherwise guess, and a duel seed.
 */
export function cryptoRandom(): number {
  const buffer = new Uint32Array(1);
  crypto.getRandomValues(buffer);
  return (buffer[0] ?? 0) / 0x100000000;
}

/** A fresh 32-bit seed for a duel or a rematch. */
export function newDuelSeed(random: () => number = cryptoRandom): number {
  return Math.floor(random() * 0x100000000) >>> 0;
}

/** The page's own origin, which is also the relay's — see `signalUrl`. Falls back for a DOM-less test. */
export function pageOrigin(): string {
  return typeof window === "undefined" ? "http://localhost" : window.location.origin;
}

export const ROOM_PARAM = "room";

/** The `/duel` URL that joins a room — what the host copies and sends to the other player. */
export function duelRoomPath(room: string): string {
  return `/duel?${ROOM_PARAM}=${encodeURIComponent(room)}`;
}

export function duelRoomLink(origin: string, room: string): string {
  return new URL(duelRoomPath(room), origin).toString();
}

/** The room named in a query string, or `undefined` for a bare `/duel`. */
export function parseDuelRoom(search: SearchLike): string | undefined {
  const raw = search[ROOM_PARAM];
  const value = Array.isArray(raw) ? raw[0] : raw;
  return value && value.length > 0 ? value : undefined;
}
